import * as React from "react";
import { CSSObject, styled, Theme } from "@mui/material/styles";
import List from "@mui/material/List";
import MuiDrawer from "@mui/material/Drawer";
import Divider from "@mui/material/Divider";
import { Fab, Tooltip } from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector } from "react-redux";
import { AppState } from "../redux/reducer/rootReducer";
import { AdminLeftMenu } from "../compenents/Layout/AdminLeftMenu";
import { CustomerLeftMenu } from "../compenents/Layout/CustomerLeftMenu";
import { DealerLeftMenu } from "../compenents/Layout/DealerLeftMenu";
import {
  SubscriptionFeatureName,
  useHasSubscriptionFeature,
} from "../services/featurePermissions/subscriptionFeatures";
export interface AppLeftMenuProps {
  setOpen: any;
  open: any;
}
export interface LeftMenuProps {}

const drawerWidth = 240;
const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.enteringScreen * 2.5,
  }),
  overflowX: "hidden",
  position: "fixed",
  zIndex: theme.zIndex.drawer + 1,
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.enteringScreen * 2.5,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
  position: "fixed",
  zIndex: theme.zIndex.drawer + 1,
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

export const AppLeftMenu: React.FunctionComponent<LeftMenuProps> = () => {
  const { userType } = useSelector((state: AppState) => state.authReducer);
  const hasChatWidget = useHasSubscriptionFeature(
    SubscriptionFeatureName.ChatWidget,
  );
  const [open, setOpen] = React.useState(false);
  const [supportWidgetOpen, setSupportWidgetOpen] = React.useState(false);

  // Keep the original support button hidden, but respect when it should be shown
  React.useEffect(() => {
    const hideOriginalButton = () => {
      // Only hide if the widget is not supposed to be open
      if (!supportWidgetOpen) {
        const teamSupportButton = document.getElementById(
          "designstudio-button",
        ) as HTMLElement;
        if (teamSupportButton) {
          teamSupportButton.style.display = "none";
        }
      }
    };

    hideOriginalButton();
    // Check periodically in case the button loads later, but respect the open state
    const interval = setInterval(hideOriginalButton, 500);

    return () => clearInterval(interval);
  }, [supportWidgetOpen]); // Add supportWidgetOpen as dependency

  // Detect when support button is clicked and chat opens
  React.useEffect(() => {
    if (!supportWidgetOpen) return;

    const teamSupportButton = document.getElementById(
      "designstudio-button",
    ) as HTMLElement;

    if (teamSupportButton) {
      const handleButtonClick = (e: MouseEvent) => {
        // When support button is clicked, close both button and MUI icon after chat opens
        setTimeout(() => {
          setSupportWidgetOpen(false);
          if (teamSupportButton) {
            teamSupportButton.style.display = "none";
          }
        }, 500);
      };

      teamSupportButton.addEventListener("click", handleButtonClick);

      return () => {
        teamSupportButton.removeEventListener("click", handleButtonClick);
      };
    }
  }, [supportWidgetOpen]);

  const toggleSupportWidget = () => {
    const newState = !supportWidgetOpen;
    setSupportWidgetOpen(newState);

    const teamSupportWidget = document.getElementById(
      "designstudio-button",
    ) as HTMLElement;

    if (teamSupportWidget) {
      if (newState) {
        teamSupportWidget.style.display = "unset";
      } else {
        teamSupportWidget.style.display = "none";
      }
    }
  };

  const handleMouseEnter = () => {
    setOpen(true);
  };
  const handleMouseLeave = () => {
    setOpen(false);
  };

  return (
    <>
      <Drawer
        sx={{
          width: drawerWidth,
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            bgcolor: "#1e1e40",
            color: "#FFFFFF",
          },
        }}
        variant="permanent"
        anchor="left"
        open={open}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Divider sx={{ bgcolor: "white", mt: 8 }} />
        <List sx={{ mt: 1 }}>
          {userType === "Admin" ? (
            <AdminLeftMenu />
          ) : userType === "Customer" || userType === "PortalCustomer" ? (
            <CustomerLeftMenu />
          ) : userType === "User" ? (
            <>
              <DealerLeftMenu handleCloseSidebar={handleMouseLeave} />
            </>
          ) : (
            ""
          )}
        </List>
        <Divider sx={{ bgcolor: "white" }} />
      </Drawer>

      {/* Custom Support Widget Icon - Click to toggle support */}
      {hasChatWidget && (
        <Tooltip
          title={supportWidgetOpen ? "Close Support" : "Chat Support"}
          placement="right"
        >
          <Fab
            color="primary"
            aria-label="support chat"
            onClick={toggleSupportWidget}
            sx={{
              position: "fixed",
              bottom: 20,
              left: 5,
              zIndex: 9999,
              bgcolor: "#3498db",
              color: "#fff",
              transition: "left 0.3s ease",
              "&:hover": { bgcolor: "#3498db" },
            }}
          >
            {supportWidgetOpen ? <CloseIcon /> : <ChatIcon />}
          </Fab>
        </Tooltip>
      )}
    </>
  );
};
